import { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, RefreshCw, Search, User, AlertTriangle } from "lucide-react";
import ConfirmDelete from "@/components/ConfirmDelete";
import ImageUpload from "@/components/ImageUpload";
import Thumb from "@/components/Thumb";
import Rappels from "@/components/Rappels";

export const ABONNEMENTS = { mensuel: "Mensuel (30 j)", trimestriel: "Trimestriel (90 j)", annuel: "Annuel (365 j)" };
const STATUTS = {
  actif: { label: "Actif", cls: "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" },
  expire_bientot: { label: "Expire bientôt", cls: "border-amber-500/40 text-amber-600 dark:text-amber-400" },
  expire: { label: "Expiré", cls: "border-red-500/40 text-red-600 dark:text-red-400" },
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const EMPTY = { nom: "", prenom: "", telephone: "", email: "", abonnement: "mensuel", date_debut: todayISO(), notes: "", image_path: null };
const fmtJour = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR") : "—");

export default function Membres() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [filtre, setFiltre] = useState("tous");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [renew, setRenew] = useState(null);
  const [renewType, setRenewType] = useState("mensuel");
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    return api.get("/membres").then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const openForm = (m) => {
    setEditing(m || null);
    setForm(m ? { nom: m.nom, prenom: m.prenom, telephone: m.telephone, email: m.email || "", abonnement: m.abonnement, date_debut: m.date_debut, notes: m.notes || "", image_path: m.image_path || null } : { ...EMPTY, date_debut: todayISO() });
    setDialog(true);
  };

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      setDialog(false);
      setRenew(null);
      setToDelete(null);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(
    () => (editing ? api.put(`/membres/${editing.id}`, { ...form, email: form.email || null }) : api.post("/membres", { ...form, email: form.email || null })),
    editing ? "Membre modifié" : "Membre ajouté",
  );
  const renouveler = () => run(() => api.post(`/membres/${renew.id}/renouveler`, { abonnement: renewType }), "Abonnement renouvelé");
  const remove = () => run(() => api.delete(`/membres/${toDelete.id}`), "Membre supprimé");

  if (!items) return <div className="text-muted-foreground text-sm animate-pulse" data-testid="membres-loading">Chargement…</div>;

  const alertes = items.filter((m) => m.statut !== "actif");
  const visibles = items
    .filter((m) => filtre === "tous" || m.statut === filtre)
    .filter((m) => `${m.nom} ${m.prenom} ${m.telephone}`.toLowerCase().includes(search.toLowerCase()));

  const inputCls = "bg-background border-border text-foreground";

  return (
    <div className="space-y-6 animate-fade-up" data-testid="membres-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Membres</h1>
          <p className="text-sm text-muted-foreground mt-1">Abonnés de la salle, échéances et renouvellements</p>
        </div>
        {can("membres_gerer") && (
        <Button data-testid="add-membre-button" onClick={() => openForm(null)} className="bg-emerald-500 hover:bg-emerald-600 text-white">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un membre
        </Button>
        )}
      </div>

      {alertes.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="membres-alert-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {alertes.length} abonnement(s) à renouveler : {alertes.slice(0, 5).map((m) => `${m.prenom} ${m.nom}`).join(", ")}{alertes.length > 5 ? "…" : ""}
          </p>
        </div>
      )}

      {can("membres_gerer") && <Rappels refreshKey={refreshKey} />}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, prénom, téléphone…" data-testid="membres-search-input" className={`pl-9 w-64 ${inputCls}`} />
        </div>
        <Select value={filtre} onValueChange={setFiltre}>
          <SelectTrigger data-testid="membres-filter-select" className={`w-48 ${inputCls}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border text-foreground">
            <SelectItem value="tous">Tous</SelectItem>
            <SelectItem value="actif">Actifs</SelectItem>
            <SelectItem value="expire_bientot">Expirent bientôt</SelectItem>
            <SelectItem value="expire">Expirés</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="border-border text-muted-foreground" data-testid="membres-count">{visibles.length} membre(s)</Badge>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground">Membre</TableHead>
                <TableHead className="text-muted-foreground">Téléphone</TableHead>
                <TableHead className="text-muted-foreground">Abonnement</TableHead>
                <TableHead className="text-muted-foreground">Début</TableHead>
                <TableHead className="text-muted-foreground">Fin</TableHead>
                <TableHead className="text-muted-foreground">Statut</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibles.length === 0 && (
                <TableRow className="border-border">
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8" data-testid="membres-empty">Aucun membre</TableCell>
                </TableRow>
              )}
              {visibles.map((m) => (
                <TableRow key={m.id} className="border-border" data-testid={`membre-row-${m.id}`}>
                  <TableCell className="font-medium text-foreground">
                    <div className="flex items-center gap-3">
                      <Thumb path={m.image_path} alt={m.nom} icon={User} className="w-9 h-9" />
                      <div>
                        {m.prenom} {m.nom}
                        {m.email && <span className="block text-xs text-muted-foreground font-normal">{m.email}</span>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-foreground/80">{m.telephone}</TableCell>
                  <TableCell className="text-foreground/80 capitalize">{m.abonnement}</TableCell>
                  <TableCell className="text-foreground/80">{fmtJour(m.date_debut)}</TableCell>
                  <TableCell className="text-foreground/80">
                    {fmtJour(m.date_fin)}
                    <span className="block text-[11px] text-muted-foreground">
                      {m.jours_restants < 0 ? `il y a ${-m.jours_restants} j` : `dans ${m.jours_restants} j`}
                    </span>
                  </TableCell>
                  <TableCell><Badge variant="outline" className={STATUTS[m.statut].cls} data-testid={`membre-statut-${m.id}`}>{STATUTS[m.statut].label}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {can("membres_gerer") && (
                    <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-emerald-600" data-testid={`renew-membre-${m.id}`} onClick={() => { setRenew(m); setRenewType(m.abonnement); }} title="Renouveler">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    )}
                    {can("membres_gerer") && (
                    <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-emerald-600" data-testid={`edit-membre-${m.id}`} onClick={() => openForm(m)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    )}
                    {can("membres_supprimer") && (
                      <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-red-600" data-testid={`delete-membre-${m.id}`} onClick={() => setToDelete(m)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle data-testid="membre-dialog-title">{editing ? "Modifier le membre" : "Ajouter un membre"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <ImageUpload value={form.image_path} onChange={(v) => setForm({ ...form, image_path: v })} testid="membre-image-upload" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Prénom</Label>
              <Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="membre-prenom-input" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Nom</Label>
              <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="membre-nom-input" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Téléphone</Label>
              <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="77 000 00 00" data-testid="membre-telephone-input" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Email (optionnel)</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="membre-email-input" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Abonnement</Label>
              <Select value={form.abonnement} onValueChange={(v) => setForm({ ...form, abonnement: v })}>
                <SelectTrigger data-testid="membre-abonnement-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  {Object.entries(ABONNEMENTS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Date de début</Label>
              <Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} data-testid="membre-date-debut-input" className={inputCls} />
            </div>
            <div className="space-y-2 col-span-2">
              <Label className="text-foreground/80">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="membre-notes-input" className={inputCls} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="border-border text-foreground/80">Annuler</Button>
            <Button onClick={save} disabled={busy || !form.nom || !form.prenom || !form.telephone} data-testid="save-membre-button" className="bg-emerald-500 hover:bg-emerald-600 text-white">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renew} onOpenChange={() => setRenew(null)}>
        <DialogContent className="bg-card border-border text-foreground max-w-sm">
          <DialogHeader>
            <DialogTitle data-testid="renew-dialog-title">Renouveler — {renew?.prenom} {renew?.nom}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-muted-foreground">Fin actuelle : <span className="text-foreground font-medium">{fmtJour(renew?.date_fin)}</span>. Le nouvel abonnement démarre à la fin de l'actuel (ou aujourd'hui s'il est expiré).</p>
            <div className="space-y-2">
              <Label className="text-foreground/80">Formule</Label>
              <Select value={renewType} onValueChange={setRenewType}>
                <SelectTrigger data-testid="renew-abonnement-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  {Object.entries(ABONNEMENTS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenew(null)} className="border-border text-foreground/80">Annuler</Button>
            <Button onClick={renouveler} disabled={busy} data-testid="confirm-renew-button" className="bg-emerald-500 hover:bg-emerald-600 text-white">
              <RefreshCw className="w-4 h-4 mr-2" /> Renouveler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={!!toDelete} onOpenChange={() => setToDelete(null)} onConfirm={remove} title={toDelete ? `${toDelete.prenom} ${toDelete.nom}` : ""} busy={busy} />
    </div>
  );
}
